'use client';

import type { PostGridProps } from '@routeroyal/theme-core';
import { RouteRoyalPostCard } from './RouteRoyalPostCard';

/**
 * Route Royal Post Grid
 * 
 * Responsive grid layout with:
 * - Flexible column configurations
 * - List and masonry variants
 * - Featured first post option
 */
export function RouteRoyalPostGrid({
  posts,
  variant = 'grid',
  columns = 3,
  cardVariant = 'default',
  showExcerpt = true,
  showCategory = true,
  showAuthor = true,
  emptyMessage,
  locale = 'en',
  basePath = '/blog',
  className = '',
  messages,
}: PostGridProps) {
  if (!posts || posts.length === 0) {
    return (
      <div className={`text-center py-16 ${className}`}>
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        </div>
        <p className="text-gray-500 text-lg font-medium">
          {emptyMessage || messages?.noPosts || 'No posts found'}
        </p>
        {messages?.noPostsDescription && (
          <p className="text-gray-400 mt-2">{messages.noPostsDescription}</p>
        )}
      </div>
    );
  }

  // List variant
  if (variant === 'list') {
    return (
      <div className={`flex flex-col gap-4 ${className}`}>
        {posts.map((post) => (
          <RouteRoyalPostCard
            key={post.id}
            post={post}
            variant="horizontal"
            showExcerpt={showExcerpt}
            showCategory={showCategory}
            showAuthor={showAuthor}
            locale={locale}
            basePath={basePath}
            {...(messages ? { messages } : {})}
          />
        ))}
      </div>
    );
  }

  // Masonry variant
  if (variant === 'masonry') {
    return (
      <div
        className={className}
        style={{
          columnCount: columns,
          columnGap: '1.5rem',
        }}
      >
        {posts.map((post) => (
          <div key={post.id} className="break-inside-avoid mb-6">
            <RouteRoyalPostCard
              post={post}
              variant={cardVariant}
              showExcerpt={showExcerpt}
              showCategory={showCategory}
              showAuthor={showAuthor}
              locale={locale}
              basePath={basePath}
              {...(messages ? { messages } : {})}
            />
          </div>
        ))}
      </div>
    );
  }

  // Grid variant (default)
  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }[columns];

  return (
    <div className={`grid ${gridColsClass} gap-6 ${className}`}>
      {posts.map((post, index) => (
        <RouteRoyalPostCard
          key={post.id}
          post={post}
          variant={index === 0 && columns === 3 && posts.length > 3 ? 'featured' : cardVariant}
          showExcerpt={showExcerpt}
          showCategory={showCategory}
          showAuthor={showAuthor}
          locale={locale}
          basePath={basePath}
          {...(messages ? { messages } : {})}
          className={
            index === 0 && columns === 3 && posts.length > 3
              ? 'md:col-span-2'
              : ''
          }
        />
      ))}
    </div>
  );
}
